import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import Header from "../components/Header"
import Layout from "../components/Layout"
import IntegrationGuideStripe from "../components/integrations/IntegrationGuideStripe"
import { btn, btnPrimary } from "../ds/button.module.css"
import { WhiteArrowRight } from "../ds/Shapes"
import PremagicNewsSection from "../components/PremagicNews"

const avatarStudioFeatures = [
  {
    id: 1,
    title: `Fun <span class="text-[#7251F4] font-semibold">shareable</span> content`,
    description:
      "Create playful, shareable AI avatars that turn mundane event networking into a fun, memorable brand experience",
    image: (
      <StaticImage
        src="../images/avatar-studio/share-fun.png"
        alt="Fun shareable content"
        className="w-full h-full object-contain rounded-2xl"
      />
    ),
  },
  {
    id: 2,
    title: `A <span class="text-[#7251F4] font-semibold">branded</span> keepsake`,
    description:
      "Embed your event's brand into every quirky, personalized avatar that keep your brand top-of-mind long after the event ends.",
    image: (
      <StaticImage
        src="../images/avatar-studio/branded-keepsake.png"
        alt="Branded Keepsake"
        className="w-full h-full object-contain rounded-2xl"
      />
    ),
  },
  {
    id: 2,
    title: `Fuel <span class="text-[#7251F4] font-semibold">viral</span> conversations`,
    description:
      "Encourage social interaction with out-of-this-world avatars that your attendees won’t hesitate to flaunt.",
    image: (
      <StaticImage
        src="../images/avatar-studio/fuel-viral.png"
        alt="Fuel Viral"
        className="w-full h-full object-contain rounded-2xl"
      />
    ),
  },
]

export default function index() {
  return (
    <Layout
      title="AI Avatar Studio - Create Event Avatars & Posters"
      description="Generate personalized avatars and shareable event posters in seconds. AI-powered creative tools for enhanced attendee engagement."
      keywords={[
        "AI avatar creation, event avatar generator, personalized event avatars, shareable poster creation, AI poster generator, event creative tools, avatar marketing events, custom avatar studio, event poster maker, AI-generated avatars, social avatar sharing, event personalization tools, automated poster design, digital avatar solutions, creative event content",
      ]}
      breadcrumbs={[
        { name: "Home", url: "/" },
        { name: "Avatar Studio", url: "/avatar-studio/" },
      ]}
    >
      <div className="">
        <div className="" style={{ backgroundColor: "#7251F4" }}>
          <Header isWhiteLogo />
        </div>

        <section className="md:py-28 relative bg-[#7251F4]">
          <div className="bg-gradient-to-t from-[#7251F4] to-transparent h-64 w-full absolute bottom-0 right-0 z-10"></div>

          <div className="px-5 md:px-0 mx-auto md:w-[1080px] flex items-start pt-10 md:pt-0 h-[calc(100dvh-80px)] md:h-[500px]">
            <div className="md:w-[700px] text-left">
              <h1 className="text-3xl md:text-7xl font-bold mb-4 text-white !leading-[1.3]">
                Add a playful twist with
                <span className="text-[#61F2C2]"> AI Avatars</span>
              </h1>
              <p className="text-white text-lg mb-5">
                Let your attendees transform into their most fun,
                <br /> unforgettable
                <span className="text-[#61F2C2]"> avatars!</span>
              </p>
              <Link
                to="/request-a-demo/"
                className={`${btn} ${btnPrimary} text-xs mt-5 mb-10 md:mb-0 relative w-max group`}
              >
                <span className="mr-4 ml-2">Get Started</span>
                <WhiteArrowRight className="transition duration-300 ease-in-out group-hover:translate-x-2 mr-2" />
              </Link>
            </div>
            <div className="w-11/12 md:w-6/12 absolute right-10 bottom-0">
              <StaticImage
                src="../images/avatar-studio/ai_avatar_hero.png"
                alt="ai-avatar-studio-events-hero"
              />
            </div>
          </div>
        </section>

        <section className="md:pt-10 pt-10 md:pt-0 md:h-[500px] flex items-center">
          <div className="px-5 md:px-0 mx-auto md:w-[1080px] text-center">
            <h2 className="text-2xl md:text-5xl font-bold mb-8 text-[#7251F4]">
              What are AI-powered event avatars?
            </h2>
            <p className="text-black mb-5 leading-[1.3] w-full md:w-[52%] mx-auto text-lg md:text-xl">
              Whether it's a space cowboy or warrior princess, these AI-powered
              avatars turn passive attendees into engaged brand
              ambassadors—effortlessly.
            </p>
            <Link
              to="/request-a-demo/"
              className={`${btn} ${btnPrimary} text-xs mt-5 mb-10 md:mb-0 relative w-max group`}
            >
              Get Started
            </Link>
          </div>
        </section>

        <section className="pt-10 md:pt-24 px-4 xl:px-[250px]">
          {avatarStudioFeatures.map((item, idx) => (
            <div
              key={idx}
              className={`flex flex-col md:flex-row items-center justify-between p-6 rounded-3xl overflow-hidden mb-10 
              ${idx % 2 !== 0 ? "md:flex-row-reverse" : ""}`}
            >
              <div
                className={`w-full md:w-[600px] mx-8 ${
                  idx % 2 !== 0 ? "md:!ml-14" : ""
                }`}
              >
                <h3
                  className="mb-4 md:mb-6 font-bold text-2xl md:text-3xl text-black"
                  dangerouslySetInnerHTML={{ __html: item.title }}
                ></h3>
                <p className="text-base md:text-lg mb-6 md:mb-0">
                  {item.description}
                </p>
                <Link
                  to="/request-a-demo/"
                  className={`${btn} ${btnPrimary} text-xs mt-5 mb-10 md:mb-0 relative w-max group`}
                >
                  Get Started
                </Link>
              </div>
              <div className="w-full md:w-1/2 ">{item.image}</div>
            </div>
          ))}
        </section>

        <section className="md:my-24">
          <div className="px-5 md:px-0 mx-auto flex py-10 md:items-center flex-wrap md:w-[1080px]">
            <div className="w-1/2 mb-20 md:mb-0 md:w-1/4 text-center">
              <h4
                style={{ color: "#FF6550" }}
                className="text-5xl font-bold my-5"
              >
                3x
              </h4>
              <p className="w-full mx-auto text-center">
                more attendee engagement than traditional activations
              </p>
            </div>
            <div className="w-1/2 mb-20 md:mb-0 md:w-1/4 text-center mx-auto">
              <h4
                style={{ color: "#FF6550" }}
                className="text-5xl font-bold my-8 md:my-5 "
              >
                50%
              </h4>
              <p className="w-full mx-auto text-center">
                attendees share their avatars on social media
              </p>
            </div>
            <div className="w-1/2 md:w-1/4 text-center">
              <h4
                style={{ color: "#FF6550" }}
                className="text-5xl font-bold my-5"
              >
                2x
              </h4>
              <p className="w-full mx-auto text-center">
                organizers say it made their event more memorable
              </p>
            </div>
          </div>
        </section>

        <div
          className="rounded-2xl xl:w-1/2 w-11/12 mt-20 mx-auto py-8 px-8 mb-10 flex md:items-center items-start md:flex-row flex-col md:justify-between justify-start"
          style={{ backgroundColor: "#7251F4" }}
        >
          <div className="md:mr-6 mb-4 md:mb-0">
            <h3 className="text-2xl font-extrabold text-white">
              Ditch the boring. bring on the fun!
            </h3>
            <p className="text-white mt-2 md:mb-5 text-lg">
              Turn your event into an adventure.
            </p>
            <Link
              to="/request-a-demo/"
              className={`${btn} ${btnPrimary} text-xs mb-10 md:mb-0 relative md:w-max group md:mt-0 mt-10 mb-0 !w-full`}
            >
              SEE HOW IT WORKS
            </Link>
          </div>
          <div className="w-full md:w-1/2">
            <StaticImage
              src="../images/avatar-studio/avatar-battle.png"
              alt="ai-avatar-studio-events-battle"
            />
          </div>
        </div>

        {/* Cross-links to related products */}
        <section className="py-16 bg-gray-50">
          <div className="px-5 md:px-0 mx-auto md:w-[1080px]">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-black mb-10">
              Explore More Premagic Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/smart-photo-distribution-for-seamless-events/"
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <h3 className="font-bold text-lg text-black mb-2">
                  Photo Distribution
                </h3>
                <p className="text-sm text-gray-600">
                  AI-powered facial recognition delivers event photos instantly
                  to every attendee.
                </p>
              </Link>
              <Link
                to="/create-event-marketing-posters/"
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <h3 className="font-bold text-lg text-black mb-2">
                  Marketing Posters
                </h3>
                <p className="text-sm text-gray-600">
                  Create personalized, shareable posters that turn attendees
                  into promoters.
                </p>
              </Link>
              <Link
                to="/event-networking-app/"
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <h3 className="font-bold text-lg text-black mb-2">
                  Event Networking
                </h3>
                <p className="text-sm text-gray-600">
                  Smart matchmaking and AI-powered connections for meaningful
                  attendee engagement.
                </p>
              </Link>
            </div>
          </div>
        </section>
        <PremagicNewsSection />
        <IntegrationGuideStripe context="Wondering how AI Avatars plug into your event registration platform?" />
      </div>
    </Layout>
  )
}
